import React, { useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';
// import sections
import Hero from '../components/sections/Hero';
import WhatHappensAfterSection from '../components/sections/WhatHappensAfterSection';
import FeaturesSplit from '../components/sections/FeaturesSplit';
import McpConnections from '../components/sections/McpConnections';
import Testimonial from '../components/sections/Testimonial';
import Video from '../components/sections/Video';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
// import ScrollReveal from '../utils/ScrollReveal';

import '../assets/scss/style.scss';
import Pricing from '../components/sections/Pricing';
import FAQ from '../components/sections/FAQ';
import BottomCta from '../components/sections/BottomCta';
import { scrollToSection } from '../utils/navigationUtils';
import FiIcons from '../fiIconMap';
import PromoCodeBanner from '../../../components/PromoCodeBanner/PromoCodeBanner';
import { MARKETING_PAGE_META } from '../../../data/marketingPageMeta';

const LandingHome = () => {
    const location = useLocation();

    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        
        // Handle promo code from URL FIRST (highest priority)
        // Priority: promocode > coupon > promo (explicit parameters only)
        // IMPORTANT: Never use 'ref' as a promo code - it's for external referrals only
        const promoCodeParam = urlParams.get('promocode') || 
                               urlParams.get('coupon') || 
                               urlParams.get('promo');
        
        if (promoCodeParam) {
            // Always overwrite any existing promo code in localStorage when promocode is in URL
            localStorage.setItem("promoCode", promoCodeParam);
        }
        
        // Handle internal referrals: 'referral' parameter (from other Luckynote users)
        // These are validated against existing users in the database
        const internalReferralParam = urlParams.get('referral');
        if (internalReferralParam) {
            localStorage.setItem("referralId", internalReferralParam);
        }
        
        // Handle external referrals: 'ref' parameter (from different websites)
        // These are stored as discoverySource and don't need validation
        const externalRefParam = urlParams.get('ref');
        if (externalRefParam) {
            localStorage.setItem("externalRef", externalRefParam);
        }
    }, [location.search]);


    useEffect(() => {
        // Handle scroll to FAQ if coming from another page
        if (location.state?.scrollToFAQ) {
            setTimeout(() => {
                scrollToSection('faq');
            }, 100); // Small delay to ensure content is rendered
        }
    }, [location]);

    // Handle initial hash navigation on page load (e.g., ?ref=tonybf#pricing)
    // Also handles browser back/forward navigation with hash changes
    useEffect(() => {
        const hash = location.hash;
        if (hash && hash.startsWith('#')) {
            const sectionId = hash.substring(1); // Remove the '#' prefix
            // Small delay to ensure DOM is fully rendered
            const timer = setTimeout(() => {
                scrollToSection(sectionId);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [location.hash]); // Run when hash changes (including browser navigation)

    return (
        <CustomStyle>
            <PromoCodeBanner />
            <Header navPosition="right" className="reveal-from-bottom" />
            <main className="site-content">
                <Hero />
                <WhatHappensAfterSection />
                <Testimonial topDivider />
                <FeaturesSplit invertMobile imageFill />
                <McpConnections />
                <SaveFromAnywhere />
                <UseCases />
                <Video topDivider />
                <Pricing topDivider />
                <AboutUs />
                <FAQ topDivider />
                <BottomCta topDivider />
                {/* <Cta split /> */}
            </main>
            <Footer />
        </CustomStyle>
    );
};

const CustomStyle = styled.div`
    body {
        font-size: 1rem;
    }

    /* Adjust header position when promo banner is visible */
    /* Banner height: 12px padding top + 12px padding bottom + ~24px content = ~48px */
    body.promo-banner-visible .site-header {
        top: 48px !important;
    }

    /* Clean section spacing */
    .landing-section {
        padding: 120px 0;

        @media (max-width: 768px) {
            padding: 80px 0;
        }
    }

    .landing-section + .landing-section {
        border-top: 1px solid rgba(0, 0, 0, 0.06);
    }

    .mcp-connections {
        background: #fbf7f2;
    }

    .mcp-connections-inner {
        max-width: 900px;
        margin: 0 auto;
        text-align: center;
    }

    .mcp-connections h2 {
        margin: 0 0 18px;
        font-size: 36px;
        font-weight: 700;
        letter-spacing: -0.02em;
        line-height: 1.2;
        color: #1a1a1a;
    }

    .mcp-connections-intro > p {
        max-width: 660px;
        margin: 0 auto;
        color: #6c757d;
        font-size: 17px;
        line-height: 1.65;
    }

    .mcp-connections-cards {
        display: flex;
        max-width: 772px;
        flex-wrap: wrap;
        justify-content: center;
        gap: 28px;
        margin: 40px auto 0;
    }

    .mcp-connection-mark {
        display: inline-flex;
        width: 72px;
        height: 72px;
        align-items: center;
        justify-content: center;
        border: 1px solid rgba(20, 17, 15, 0.1);
        border-radius: 18px;
        background: rgba(255, 255, 255, 0.88);
        font-size: 38px;
        box-shadow: 0 8px 18px rgba(59, 46, 35, 0.1);
        transition: transform 0.2s ease, box-shadow 0.2s ease;
    }

    a.mcp-connection-mark:hover,
    a.mcp-connection-mark:focus-visible {
        box-shadow: 0 12px 28px rgba(59, 46, 35, 0.16);
        transform: translateY(-4px);
    }

    .mcp-connection-image {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }

    @media (max-width: 768px) {
        .mcp-connections-inner {
            max-width: 580px;
        }

        .mcp-connections-cards {
            max-width: 312px;
            gap: 20px 28px;
            margin: 36px auto 0;
        }

        .mcp-connections h2 {
            font-size: 28px;
        }

        .mcp-connection-mark {
            width: 64px;
            height: 64px;
            border-radius: 16px;
            font-size: 34px;
        }
    }

    /* Subtle hover effects */
    .feature-card {
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        &:hover {
            transform: translateY(-4px);
        }
    }

    .tiles-item-inner {
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        &:hover {
            transform: translateY(-4px);
        }
    }

    .use-case-card:hover,
    .use-case-card:focus-visible {
        transform: translateY(-3px);
    }

    .landing-founder-link {
        display: inline-flex;
        align-items: center;
        color: #1a1a1a;
        text-decoration: none;
        font-size: 15px;
        font-weight: 500;
        padding: 10px 20px;
        border: 1px solid #ddd;
        border-radius: 100px;
        transition: border-color 0.2s ease, color 0.2s ease;
    }

    .landing-founder-link:hover {
        border-color: #1a1a1a;
        color: #1a1a1a;
    }
`;

// Use Cases section
const UseCases = () => {
    const isMobile = useMediaQuery({ maxWidth: 767 });
    const useCases = [
        {
            path: '/use-cases/second-brain',
            title: 'Second brain',
            description: 'Let links, notes, files, screenshots, and voice notes become a personal knowledge base over time.'
        },
        {
            path: '/use-cases/read-it-later',
            title: 'Saved links you can actually find',
            description: 'Keep articles, reels, videos, and posts with enough context to remember why they mattered.'
        },
        {
            path: '/use-cases/voice-notes',
            title: 'Voice notes',
            description: 'Say the thought before it disappears. Luckynote keeps the transcript searchable for later.',
            iconColor: '#8b5cf6',
            iconBackground: 'rgba(139, 92, 246, 0.1)'
        },
        {
            path: '/use-cases/recipes',
            title: 'Recipes and travel',
            description: 'Save the reel, screenshot, link, place, or reminder while it is still interesting.',
            icon: 'FiMapPin',
            iconColor: '#ef4444',
            iconBackground: 'rgba(239, 68, 68, 0.1)'
        },
        {
            path: '/use-cases/journaling',
            title: 'Journaling',
            description: 'Keep daily notes, voice memos, and photos in one place your future self can search.'
        },
        {
            path: '/for/mental-health',
            title: 'Get it out of your head',
            description: 'Sometimes saving the thought is enough. Luckynote keeps it there without asking you to organize it.'
        }
    ].map((useCase) => {
        const page = MARKETING_PAGE_META.find((candidate) => candidate.path === useCase.path);
        return {
            ...useCase,
            icon: useCase.icon || page?.icon,
            color: useCase.iconColor || page?.iconColor || '#667eea',
            background: useCase.iconBackground || page?.iconBackground || 'rgba(102, 126, 234, 0.1)'
        };
    }).filter((useCase) => useCase.icon);

    return (
        <section id="usecases" className="landing-section" style={{ backgroundColor: '#fcf9f7' }}>
            <div className="container">
                <div className="center-content" style={{ maxWidth: '900px', margin: '0 auto' }}>
                    <h2 className="mt-0" style={{
                        marginBottom: isMobile ? '48px' : '56px',
                        fontSize: isMobile ? '28px' : '36px',
                        fontWeight: '700',
                        letterSpacing: '-0.02em',
                        textAlign: 'center',
                        color: '#1a1a1a'
                    }}>
                        One place for the things your future self will need
                    </h2>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                        gap: '0',
                        columnGap: isMobile ? '0' : '48px',
                    }}>
                        {useCases.map((useCase, index) => (
                            <Link key={useCase.path} to={useCase.path} className="use-case-card" style={{
                                display: 'block',
                                padding: isMobile ? '24px 0' : '32px',
                                textAlign: 'left',
                                textDecoration: 'none',
                                borderBottom: index < useCases.length - (isMobile ? 1 : (useCases.length % 2 || 2)) ? '1px solid rgba(20, 17, 15, 0.06)' : 'none',
                                borderRadius: '12px',
                                transition: 'transform 0.2s ease, opacity 0.2s ease'
                            }}>
                                <div style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '10px',
                                    backgroundColor: useCase.background,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginBottom: '14px'
                                }}>
                                    {React.createElement(FiIcons[useCase.icon], {
                                        size: 18,
                                        color: useCase.color,
                                        'aria-hidden': 'true'
                                    })}
                                </div>
                                <h3 style={{
                                    margin: '0 0 8px',
                                    fontSize: '18px',
                                    fontWeight: '600',
                                    color: '#1a1a1a'
                                }}>
                                    {useCase.title}
                                </h3>
                                <p style={{
                                    margin: 0,
                                    fontSize: '15px',
                                    lineHeight: '1.6',
                                    color: '#6c757d'
                                }}>
                                    {useCase.description}
                                </p>
                            </Link>
                        ))}
                    </div>

                    <div style={{
                        display: 'flex',
                        gap: '18px',
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                        marginTop: isMobile ? '28px' : '36px'
                    }}>
                        <Link to="/use-cases" style={{
                            color: '#1a1a1a',
                            fontSize: '15px',
                            fontWeight: '600',
                            textDecoration: 'underline',
                            textUnderlineOffset: '0.18em'
                        }}>
                            See all use cases
                        </Link>
                        <Link to="/for" style={{
                            color: '#1a1a1a',
                            fontSize: '15px',
                            fontWeight: '600',
                            textDecoration: 'underline',
                            textUnderlineOffset: '0.18em'
                        }}>
                            See who it is for
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
};

// Save From Anywhere section
const SAVE_SOURCES = [
    { path: '/guides/save-from-instagram', label: 'Instagram' },
    { path: '/guides/save-from-tiktok', label: 'TikTok' },
    { path: '/guides/save-from-x', label: 'X' },
    { path: '/guides/save-from-whatsapp', label: 'WhatsApp' },
    { path: '/guides/save-from-web', label: 'The web' },
    { path: '/guides/save-screenshots', label: 'Screenshots' },
    { path: '/guides/save-pdfs-and-files', label: 'PDFs and files' }
];

const SaveFromAnywhere = () => {
    const isMobile = useMediaQuery({ maxWidth: 767 });

    const saveSources = SAVE_SOURCES.map((source) => {
        const page = MARKETING_PAGE_META.find((candidate) => candidate.path === source.path);
        return {
            ...source,
            icon: page?.icon,
            color: page?.iconColor,
            background: page?.iconBackground
        };
    });

    return (
        <section className="landing-section" style={{ backgroundColor: '#fbf7f2' }}>
            <div className="container">
                <div className="center-content" style={{ maxWidth: '900px', margin: '0 auto' }}>
                    <h2 className="mt-0" style={{
                        marginBottom: '16px',
                        fontSize: isMobile ? '28px' : '36px',
                        fontWeight: '700',
                        letterSpacing: '-0.02em',
                        textAlign: 'center',
                        color: '#1a1a1a'
                    }}>
                        Save from wherever the thought happens
                    </h2>
                    <p style={{
                        fontSize: '17px',
                        lineHeight: '1.65',
                        color: '#6c757d',
                        maxWidth: '560px',
                        margin: '0 auto 40px',
                        textAlign: 'center'
                    }}>
                        Share a reel, forward a link, upload a file, paste a note, or save a screenshot. Luckynote gives it a place before your attention moves on.
                    </p>

                    <SaveSourceGrid>
                        {saveSources.map((source) => {
                            const IconComponent = source.icon ? FiIcons[source.icon] : null;
                            return (
                                <SaveSourceChip key={source.path} to={source.path}>
                                    {IconComponent && (
                                        <SaveSourceIconBadge $color={source.color} $background={source.background}>
                                            <IconComponent size={14} />
                                        </SaveSourceIconBadge>
                                    )}
                                    {source.label}
                                </SaveSourceChip>
                            );
                        })}
                    </SaveSourceGrid>

                    <div style={{ textAlign: 'center', marginTop: '28px' }}>
                        <AllGuidesLink to="/guides">All guides</AllGuidesLink>
                    </div>
                </div>
            </div>
        </section>
    );
};

const SaveSourceGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 12px;
`;

const SaveSourceChip = styled(Link)`
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 8px 20px 8px 10px;
    border-radius: 100px;
    border: 1px solid rgba(20, 17, 15, 0.12);
    background: rgba(255, 255, 255, 0.58);
    backdrop-filter: blur(12px);
    font-size: 15px;
    font-weight: 600;
    text-decoration: none;
    transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;

    && {
        color: #14110f;
    }

    &&:hover {
        color: #2b88d9;
        background: rgba(255, 255, 255, 0.88);
        border-color: rgba(20, 17, 15, 0.22);
        box-shadow: 0 12px 28px rgba(59, 46, 35, 0.14);
        transform: translateY(-2px);
    }
`;

const SaveSourceIconBadge = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 8px;
    flex-shrink: 0;
    background: ${({ $background }) => $background};
    color: ${({ $color }) => $color};
`;

const AllGuidesLink = styled(Link)`
    display: inline-flex;
    align-items: center;
    font-size: 15px;
    font-weight: 600;
    text-decoration: underline;
    text-underline-offset: 0.18em;

    && {
        color: #1a1a1a;
    }

    &&:hover {
        color: #2b88d9;
    }
`;

// About Us section
const AboutUs = () => {
    const isMobile = useMediaQuery({ maxWidth: 767 });
    const textBlock = (
        <div style={{ textAlign: 'left' }}>
            <p style={{ fontSize: '17px', lineHeight: '1.8', marginBottom: '24px', color: '#555' }}>
                I had things saved everywhere - different apps, different folders, different devices. I'd spend more time trying to remember <em>where</em> something was than actually using it.
            </p>
            <p style={{ fontSize: '17px', lineHeight: '1.8', marginBottom: '24px', color: '#555' }}>
                I tried more than 100 different tools before deciding to build my own. For a while, texting myself still worked better than most of them: one thread, everything in one place. But it did not scale - and I knew I was only saving the simplest things, when I had so much more information I wanted to keep and actually use.
            </p>
            <p style={{ fontSize: '17px', lineHeight: '1.8', marginBottom: '24px', color: '#555' }}>
                So I took everything I knew about UX and the note-taking pain I'd lived with for years, and built the tool I actually wanted: <strong>something fast like messaging myself, but useful like a second brain</strong> - where I can find anything I've ever saved, without having to feel lucky about it.
            </p>
            <p style={{ fontSize: '17px', lineHeight: '1.8', marginBottom: '0', color: '#555' }}>
                That's why it's called Luckynote. Not because finding things is a gamble - but because with the right tool, <strong>you can feel lucky every single day.</strong>
            </p>
        </div>
    );
    const imageBlock = (
        <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <div style={{
                width: '180px',
                height: '180px',
                borderRadius: '50%',
                margin: '0 auto 24px',
                overflow: 'hidden',
            }}>
                <img
                    src={require('../assets/images/founder-uros.jpg')}
                    alt="Uros, Founder of Luckynote"
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                    }}
                />
            </div>
            <h3 style={{ margin: '0 0 4px', fontSize: '22px', fontWeight: '600', color: '#1a1a1a' }}>Uros</h3>
            <p style={{ margin: '0 0 24px', fontSize: '15px', color: '#999' }}>
                Founder & Developer
            </p>

            <a
                href="https://x.com/lucky_uros"
                target="_blank"
                rel="noopener noreferrer"
                className="landing-founder-link"
            >
                Follow on X
            </a>
            <a
                href="https://www.instagram.com/lucky_uros/"
                target="_blank"
                rel="noopener noreferrer"
                className="landing-founder-link"
                style={{ marginLeft: '10px' }}
            >
                Instagram
            </a>
        </div>
    );
    return (
        <section className="landing-section" style={{ backgroundColor: '#fbf7f2' }}>
            <div className="container">
                <div className="center-content" style={{ maxWidth: '900px', margin: '0 auto' }}>
                    <h2 className="mt-0" style={{
                        marginBottom: '48px',
                        textAlign: 'center',
                        fontSize: isMobile ? '28px' : '36px',
                        fontWeight: '700',
                        letterSpacing: '-0.02em',
                        color: '#1a1a1a'
                    }}>
                        Built because I needed it
                    </h2>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : '1fr 280px',
                        gap: '48px',
                        alignItems: 'start',
                    }}>
                        {isMobile ? (
                            <>
                                {imageBlock}
                                {textBlock}
                            </>
                        ) : (
                            <>
                                {textBlock}
                                {imageBlock}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default LandingHome;
